import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import NavHeader from "../components/layout/NavHeader";
import UpdatesSignup from "../components/marketing/UpdatesSignup";
import Seo from "../components/seo/Seo";
import { LIST_PRICING } from "../content/listPricing";
import { trackEvent } from "../lib/analytics";
import { productUrl } from "../lib/appUrls";
import styles from "./Pricing.module.css";

const FAQ_KEYS = ["card", "afterTrial", "ai", "cancel", "printer", "members"] as const;

export default function Pricing() {
  const { t, i18n } = useTranslation();
  const formatMoney = (unitAmount: number, currency: string, interval: string) =>
    `${new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 0,
    }).format(unitAmount / 100)}/${interval}`;

  const monthlyAmount = formatMoney(
    LIST_PRICING.monthly.unitAmount,
    LIST_PRICING.monthly.currency,
    "month"
  );
  const yearlyAmount = formatMoney(
    LIST_PRICING.yearly.unitAmount,
    LIST_PRICING.yearly.currency,
    "year"
  );
  const yearlySavingsPercent = Math.max(
    0,
    Math.round(100 * (1 - LIST_PRICING.yearly.unitAmount / (LIST_PRICING.monthly.unitAmount * 12)))
  );
  const yearlyEquivalent = useMemo(() => {
    const amount = new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: LIST_PRICING.yearly.currency.toUpperCase(),
      maximumFractionDigits: 0,
    }).format(LIST_PRICING.yearly.unitAmount / 12 / 100);
    return t("pricing.yearlyEquivalent", { amount });
  }, [i18n.language, t]);

  const faqs = FAQ_KEYS.map((key) => ({
    question: t(`pricing.faq.${key}Question`),
    answer: t(`pricing.faq.${key}Answer`),
  }));
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
  const handleStartTrial = () => trackEvent("trial_cta_clicked", { placement: "pricing" });
  const features = [
    "unlimitedLines",
    "unlimitedOrdersSkus",
    "traceabilityLabels",
    "ganttScheduler",
    "constraintEngine",
    "reports",
  ] as const;

  return (
    <>
      <Seo
        title={t("pricing.seoTitle")}
        description={t("pricing.seoDescription", { monthly: monthlyAmount, yearly: yearlyAmount })}
        path="/pricing"
        structuredData={structuredData}
      />
      <main id="main-content" className={styles.page}>
        <div className={styles.inner}>
          <NavHeader />
          <section className={styles.panel}>
            <header className={styles.header}>
              <p className={styles.kicker}>{t("pricing.kicker")}</p>
              <h1 className={styles.title}>{t("pricing.heroTitle")}</h1>
              <p className={styles.subtitle}>{t("pricing.heroSubtitle")}</p>
              <p className={styles.subtitle}>{t("pricing.audience")}</p>
            </header>
            <section className={`${styles.grid} ${styles.gridTwo}`}>
              <article className={styles.card}>
                <span className={styles.badge}>{t("billing.monthly")}</span>
                <h2 className={styles.cardTitle}>{monthlyAmount}</h2>
                <p className={styles.cardText}>{t("pricing.monthlyDescription")}</p>
                <p className={styles.trialCallout}>{t("common.trial.promise")}</p>
                <ul className={styles.list}>
                  {features.map((feature) => <li key={feature}>{t(`pricing.features.${feature}`)}</li>)}
                  <li>{t("pricing.features.fiveMembers")}</li>
                  <li>{t("pricing.features.emailSupport")}</li>
                  <li>{t("pricing.features.aiTrialYearly")}</li>
                </ul>
              </article>
              <article className={`${styles.card} ${styles.emphasisCard}`}>
                <span className={styles.badge}>
                  {t("pricing.bestValue")} · {t("pricing.savePercent", { percent: yearlySavingsPercent })}
                </span>
                <h2 className={styles.cardTitle}>{yearlyAmount}</h2>
                <p className={styles.cardText}>{yearlyEquivalent}</p>
                <p className={styles.trialCallout}>{t("common.trial.promise")}</p>
                <ul className={styles.list}>
                  {features.map((feature) => <li key={feature}>{t(`pricing.features.${feature}`)}</li>)}
                  <li>{t("pricing.features.unlimitedMembers")}</li>
                  <li>{t("pricing.features.prioritySupport")}</li>
                  <li>{t("pricing.features.csvExport")}</li>
                  <li>{t("pricing.features.aiAssistant")}</li>
                </ul>
              </article>
            </section>
            <div className={styles.trialStart}>
              <a className={styles.primaryButton} href={productUrl("/register")} onClick={handleStartTrial}>
                {t("pricing.startTrial")}
              </a>
              <p>{t("common.trial.promise")}</p>
            </div>
            <UpdatesSignup source="pricing" className={styles.updatesSignup} />
            <section className={styles.faq}>
              <h2 className={styles.faqTitle}>{t("pricing.faq.title")}</h2>
              {faqs.map((faq, index) => (
                <details key={FAQ_KEYS[index]} className={styles.faqItem} open={index === 0}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </section>
          </section>
          <footer className={styles.footer}>{t("common.footer")}</footer>
        </div>
      </main>
    </>
  );
}
